export const COOKIE_CONSENT_VERSION = '2026-09-customer-match-v1';
export const COOKIE_PREFERENCES_KEY = 'cookiePreferences';
export interface CookieChoice {
    version: string;
    marketingMeasurement: boolean;
    personalizedMarketing: boolean;
    savedAt: string;
    owner: string | null;
    pending?: boolean;
}
let volatileChoice: CookieChoice | null | undefined;
let volatileAnonymousId: string | undefined;

export const getCookieDomain = (): string => {
    try {
        const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
        if (hostname && (hostname === 'legeaaplicata.ro' || hostname.endsWith('.legeaaplicata.ro'))) {
            return '.legeaaplicata.ro';
        }
        return '';
    } catch {
        return '';
    }
};

export const readSharedCookie = (name: string): string | null => {
    try {
        const domain = getCookieDomain();
        if (!domain) return null;
        const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    } catch {
        return null;
    }
};

export const setSharedCookie = (name: string, value: string, maxAgeSeconds = 31536000) => {
    try {
        const domain = getCookieDomain();
        if (!domain) return;
        const securePart = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; domain=${domain}; SameSite=Lax${securePart}`;
    } catch { /* Cookie write might be blocked */ }
};

export const deleteSharedCookie = (name: string) => {
    try {
        const domain = getCookieDomain();
        if (!domain) return;
        document.cookie = `${name}=; path=/; domain=${domain}; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    } catch { /* ignore */ }
};

export const clearLegacyCookieConsent = () => {
    try { localStorage.removeItem("cookieConsent"); } catch { /* Storage may be unavailable. */ }
    deleteSharedCookie("cookieConsent");
};

export const hasLegacyRefusal = () => {
    try {
        if (localStorage.getItem("cookieConsent") === "false") return true;
    } catch { /* ignore */ }
    return readSharedCookie("cookieConsent") === "false";
};

export const readCookieChoice = (): CookieChoice | null => {
    if (volatileChoice !== undefined) return volatileChoice;
    try {
        let raw: string | null = null;
        let storageAvailable = false;
        try {
            raw = localStorage.getItem(COOKIE_PREFERENCES_KEY);
            storageAvailable = true;
        } catch { /* Storage may be blocked */ }

        if (storageAvailable && raw === null) {
            const fromCookie = readSharedCookie(COOKIE_PREFERENCES_KEY);
            if (fromCookie) {
                raw = fromCookie;
                try { localStorage.setItem(COOKIE_PREFERENCES_KEY, fromCookie); } catch { /* ignore */ }
            }
        }
        const value = JSON.parse(raw || 'null');
        return value?.version === COOKIE_CONSENT_VERSION && typeof value.marketingMeasurement === 'boolean'
            && typeof value.personalizedMarketing === 'boolean' ? value : null;
    } catch { return null; }
};

export const writeCookieChoice = (value: CookieChoice | null) => {
    try {
        if (value) {
            const previous = readCookieChoice();
            if (previous && previous.version === value.version && previous.owner === value.owner
                && previous.marketingMeasurement === value.marketingMeasurement
                && previous.personalizedMarketing === value.personalizedMarketing
                && !!previous.pending === !!value.pending) {
                setSharedCookie(COOKIE_PREFERENCES_KEY, JSON.stringify(value));
                return;
            }
            const str = JSON.stringify(value);
            localStorage.setItem(COOKIE_PREFERENCES_KEY, str);
            setSharedCookie(COOKIE_PREFERENCES_KEY, str);
        }
        else {
            localStorage.removeItem(COOKIE_PREFERENCES_KEY);
            deleteSharedCookie(COOKIE_PREFERENCES_KEY);
        }
        volatileChoice = undefined;
    } catch {
        volatileChoice = value; /* Session-only choice; next visit asks again. */
        if (value) {
            setSharedCookie(COOKIE_PREFERENCES_KEY, JSON.stringify(value));
        } else {
            deleteSharedCookie(COOKIE_PREFERENCES_KEY);
        }
    }
};

export const anonymousConsentId = () => {
    try {
        let saved: string | null = null;
        let storageAvailable = false;
        try {
            saved = localStorage.getItem('anonymousConsentId');
            storageAvailable = true;
        } catch { /* ignore */ }
        if (storageAvailable && !saved) {
            saved = readSharedCookie('anonymousConsentId');
            if (saved) {
                try { localStorage.setItem('anonymousConsentId', saved); } catch { /* ignore */ }
            }
        }
        if (saved) return saved;
    } catch { /* Use an ephemeral identifier when storage is blocked. */ }
    const id = volatileAnonymousId ??= crypto.randomUUID();
    try { localStorage.setItem('anonymousConsentId', id); } catch { /* Session-only audit. */ }
    setSharedCookie('anonymousConsentId', id);
    return id;
};
